<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CoverageCard, CoverageWarning } from './route-plan-coverage.utils';

interface Props {
  card: CoverageCard;
  readonly?: boolean;
  adding?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false, adding: false, disabled: false,
});
const emit = defineEmits<{ 'quick-add': [] }>();

const { t } = useI18n();

const requirementTimeLabel = computed(() => {
  if (props.card.fixedStartTime) {
    return t('routePlan.coverage.fixedAt', { time: props.card.fixedStartTime });
  }
  if (props.card.timeWindowStart && props.card.timeWindowEnd) {
    return t('routePlan.visitForm.between', {
      start: props.card.timeWindowStart, end: props.card.timeWindowEnd,
    });
  }

  return t('routePlan.coverage.noTime');
});

const coverageTimeLabel = computed(() => {
  const { coverageStartTime, coverageEndTime } = props.card;
  if (!coverageStartTime) return '';

  return coverageEndTime ? `${coverageStartTime}–${coverageEndTime}` : coverageStartTime;
});

const extraTimeLabel = computed(() => {
  if (props.card.coverageStartTime) return props.card.coverageStartTime;
  if (props.card.timeWindowStart && props.card.timeWindowEnd) {
    return t('routePlan.visitForm.between', {
      start: props.card.timeWindowStart, end: props.card.timeWindowEnd,
    });
  }

  return '';
});

const showQuickAdd = computed(
  () => !props.readonly && props.card.column === 'missing' && !props.card.blocked,
);

function warningText(warning: CoverageWarning): string {
  if (warning.kind === 'timeDrift') return t('routePlan.coverage.timeDrift');
  if (warning.kind === 'caregiverDrift') {
    return t('routePlan.coverage.caregiverDrift', { name: warning.name });
  }
  if (warning.kind === 'rejected') return t('routePlan.coverage.rejectedWarning');
  if (warning.kind === 'scheduleBlock') {
    return t('routePlan.coverage.scheduleBlock', { description: warning.description });
  }

  return t('routePlan.coverage.doubleCoverage', {
    name: warning.name, start: warning.start, end: warning.end,
  });
}
</script>

<template>
  <v-card
    elevation="2"
    class="mb-2"
    data-testid="coverage-card"
  >
    <v-card-text class="pa-3">
      <div class="d-flex align-start ga-2">
        <span class="font-weight-medium text-body-2 flex-grow-1">
          <v-icon
            icon="mdi-account-heart"
            size="x-small"
            class="me-1 text-medium-emphasis"
          />
          {{ card.patientFullName }}
        </span>
        <v-chip
          v-if="card.isExtra"
          size="x-small"
          color="warning"
          variant="tonal"
        >
          {{ t('routePlan.coverage.extraChip') }}
        </v-chip>
        <v-chip
          v-if="card.blocked"
          size="x-small"
          variant="tonal"
          prepend-icon="mdi-calendar-lock"
          data-testid="coverage-blocked-chip"
        >
          {{ t('routePlan.coverage.blockedChip') }}
        </v-chip>
        <v-tooltip
          v-if="card.warnings.length"
          location="top"
          open-on-click
        >
          <template #activator="{ props: tooltipProps }">
            <v-icon
              icon="mdi-alert"
              color="warning"
              size="small"
              v-bind="tooltipProps"
            />
          </template>
          <div
            v-for="(warning, index) in card.warnings"
            :key="index"
          >
            {{ warningText(warning) }}
          </div>
        </v-tooltip>
      </div>

      <!-- Covered by an assignment or an existing shift -->
      <template v-if="card.column === 'covered' && !card.isExtra">
        <div class="text-body-2 mt-1">
          <v-icon
            icon="mdi-check"
            color="success"
            size="x-small"
            class="me-1"
          />
          <span v-if="card.caregiverName">{{ card.caregiverName }}</span>
          <span
            v-if="coverageTimeLabel"
            class="text-medium-emphasis"
          >
            · {{ coverageTimeLabel }}
          </span>
        </div>
        <div
          v-if="card.fromExistingShift"
          class="text-caption text-medium-emphasis"
        >
          {{ t('routePlan.coverage.coveredByShift') }}
        </div>
      </template>

      <!-- Extra visit: served beyond the day's requirements -->
      <template v-else-if="card.isExtra">
        <div class="text-body-2 mt-1">
          <span v-if="card.caregiverName">{{ card.caregiverName }}</span>
          <span
            v-if="extraTimeLabel"
            class="text-medium-emphasis"
          >
            {{ card.caregiverName ? '· ' : '' }}{{ extraTimeLabel }}
          </span>
          <span
            v-if="card.durationMinutes"
            class="text-caption text-medium-emphasis"
          >
            · {{ card.durationMinutes }} min
          </span>
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ t('routePlan.coverage.noRequirement') }}
        </div>
      </template>

      <!-- Missing or in-process requirement -->
      <template v-else>
        <div class="text-body-2 mt-1">
          {{ requirementTimeLabel }}
          <span
            v-if="card.durationMinutes"
            class="text-medium-emphasis"
          >
            · {{ card.durationMinutes }} min
          </span>
        </div>
        <div
          v-if="card.column === 'inProcess' && (card.caregiverName || coverageTimeLabel)"
          class="text-caption"
        >
          <span v-if="card.caregiverName">{{ card.caregiverName }}</span>
          <span
            v-if="coverageTimeLabel"
            class="text-medium-emphasis"
          >
            {{ card.caregiverName ? '· ' : '' }}{{ coverageTimeLabel }}
          </span>
        </div>
        <div
          v-if="card.column === 'missing' && card.preferredCaregiverName"
          class="text-caption text-medium-emphasis"
        >
          {{ t('routePlan.coverage.preferred', { name: card.preferredCaregiverName }) }}
        </div>
      </template>

      <div
        v-if="card.skillName"
        class="mt-1"
      >
        <v-chip
          size="x-small"
          variant="outlined"
        >
          {{ card.skillName }}
        </v-chip>
      </div>

      <v-btn
        v-if="showQuickAdd"
        size="small"
        color="primary"
        variant="tonal"
        prepend-icon="mdi-plus"
        block
        class="mt-2"
        :loading="adding"
        :disabled="disabled"
        @click="emit('quick-add')"
      >
        {{ t('routePlan.coverage.quickAdd') }}
      </v-btn>
    </v-card-text>
  </v-card>
</template>
